/** Angular Imports */
import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NestedTreeControl } from '@angular/cdk/tree';
import {
  MatTreeNestedDataSource,
  MatTree,
  MatTreeNodeDef,
  MatTreeNode,
  MatTreeNodeToggle,
  MatNestedTreeNode,
  MatTreeNodeOutlet
} from '@angular/material/tree';
import { MatIconButton, MatButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/** Custom Imports */
import { GLAccount } from 'app/shared/models/general.model';
import { STANDALONE_SHARED_IMPORTS } from 'app/standalone-shared.module';

/**
 * Tree node used by the GL account parent selector.
 */
export interface GlAccountTreeNode {
  id: number | null;
  name: string;
  glCode: string;
  disabled: boolean;
  children: GlAccountTreeNode[];
}

/**
 * Smart tree dropdown for selecting a GL account parent.
 *
 * Shows the full chart of accounts as an expandable, searchable tree.
 * Detail accounts and explicitly excluded accounts are shown as disabled.
 * A virtual "Top Level" node lets the user make the account a root account.
 */
@Component({
  selector: 'LOSx-gl-account-tree-selector',
  templateUrl: './gl-account-tree-selector.component.html',
  styleUrls: ['./gl-account-tree-selector.component.scss'],
  imports: [
    ...STANDALONE_SHARED_IMPORTS,
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatTreeNodeToggle,
    MatNestedTreeNode,
    MatTreeNodeOutlet,
    MatIconButton,
    MatButton,
    FaIconComponent
  ]
})
export class GlAccountTreeSelectorComponent implements OnInit, OnChanges, OnDestroy {
  private translateService = inject(TranslateService);
  private elementRef = inject(ElementRef);

  /** Form control bound to the parent id. */
  @Input() inputFormControl: UntypedFormControl;
  /** Full flat list of GL accounts used to build the tree. */
  @Input() glAccountList: GLAccount[] = [];
  /** Label shown on the form field. */
  @Input() inputLabel = 'Parent';
  /** Whether a value is required. */
  @Input() required = false;
  /** IDs that cannot be selected (e.g. the account being edited and its descendants). */
  @Input() excludeIds: number[] = [];

  /** Whether the dropdown panel is open. */
  panelOpen = false;
  /** Filter term for the tree search. */
  filterControl: UntypedFormControl = new UntypedFormControl('');
  /** Display label for the currently selected parent. */
  selectedLabel = '';

  /** Nested tree control. */
  nestedTreeControl: NestedTreeControl<GlAccountTreeNode>;
  /** Nested tree data source. */
  nestedTreeDataSource: MatTreeNestedDataSource<GlAccountTreeNode>;

  private _onDestroy = new Subject<void>();

  constructor() {
    this.nestedTreeControl = new NestedTreeControl<GlAccountTreeNode>(this.getChildren);
    this.nestedTreeDataSource = new MatTreeNestedDataSource<GlAccountTreeNode>();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.panelOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.closePanel();
    }
  }

  ngOnInit(): void {
    this.filterControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.applyFilter();
    });

    this.inputFormControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.refreshSelectedLabel();
    });

    this.refreshTree();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['glAccountList'] || changes['excludeIds']) {
      this.refreshTree();
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  /**
   * Toggles the dropdown panel open/closed.
   */
  togglePanel(): void {
    this.panelOpen = !this.panelOpen;
    if (this.panelOpen) {
      this.refreshTree();
      setTimeout(() => this.expandAll(), 0);
    }
  }

  /**
   * Opens the dropdown panel.
   */
  openPanel(): void {
    this.panelOpen = true;
    this.refreshTree();
    setTimeout(() => this.expandAll(), 0);
  }

  /**
   * Closes the dropdown panel.
   */
  closePanel(): void {
    this.panelOpen = false;
  }

  /**
   * Clears the current selection and makes the account a top-level account.
   */
  clearSelection(event: MouseEvent): void {
    event.stopPropagation();
    this.inputFormControl.setValue(null);
    this.inputFormControl.markAsDirty();
    this.refreshSelectedLabel();
  }

  /**
   * Selects a tree node as the parent.
   */
  selectNode(node: GlAccountTreeNode): void {
    if (node.disabled) {
      return;
    }
    this.inputFormControl.setValue(node.id);
    this.inputFormControl.markAsDirty();
    this.refreshSelectedLabel();
    this.closePanel();
  }

  /**
   * Determines whether a node has children.
   */
  hasNestedChild = (_: number, node: GlAccountTreeNode) => node.children.length > 0;

  /**
   * Gets the children of a node.
   */
  private getChildren = (node: GlAccountTreeNode) => node.children;

  /**
   * Expands all tree nodes.
   */
  private expandAll(): void {
    this.nestedTreeControl.expandAll();
  }

  /**
   * Rebuilds the tree from the flat GL account list, applying exclusions.
   */
  private refreshTree(): void {
    const tree = this.buildTree();
    this.nestedTreeDataSource.data = tree;
    this.nestedTreeControl.dataNodes = tree;
    this.applyFilter();
    this.refreshSelectedLabel();
  }

  /**
   * Applies the search filter and refreshes the tree data source.
   */
  private applyFilter(): void {
    const term = (this.filterControl.value || '').toLowerCase().trim();
    const fullTree = this.buildTree();

    if (!term) {
      this.nestedTreeDataSource.data = fullTree;
      this.nestedTreeControl.dataNodes = fullTree;
      return;
    }

    const filteredTree = this.filterNodes(fullTree, term);
    this.nestedTreeDataSource.data = filteredTree;
    this.nestedTreeControl.dataNodes = filteredTree;
    this.expandAll();
  }

  /**
   * Builds the GL account tree from the flat list.
   */
  private buildTree(): GlAccountTreeNode[] {
    const excludeSet = new Set<number>(this.excludeIds || []);
    const accountMap = new Map<number, GLAccount>();

    for (const account of this.glAccountList) {
      accountMap.set(account.id, account);
    }

    // Recursively collect excluded descendants so they are pruned as well.
    const addDescendants = (id: number) => {
      const account = accountMap.get(id);
      if (!account) {
        return;
      }
      for (const child of this.glAccountList) {
        if (child.parentId === id) {
          excludeSet.add(child.id);
          addDescendants(child.id);
        }
      }
    };
    for (const excludedId of Array.from(excludeSet)) {
      addDescendants(excludedId);
    }

    const nodeMap = new Map<number, GlAccountTreeNode>();

    for (const account of this.glAccountList) {
      if (excludeSet.has(account.id)) {
        continue;
      }
      const disabled = account.disabled || account.usage?.value === 'DETAIL';
      nodeMap.set(account.id, {
        id: account.id,
        name: account.name,
        glCode: account.glCode,
        disabled,
        children: []
      });
    }

    const roots: GlAccountTreeNode[] = [];

    for (const node of nodeMap.values()) {
      const account = accountMap.get(node.id as number);
      const parentId = account?.parentId;
      if (!parentId || !nodeMap.has(parentId)) {
        roots.push(node);
      } else {
        const parent = nodeMap.get(parentId);
        parent?.children.push(node);
      }
    }

    const topLevelLabel = this.translateService.instant('labels.inputs.No Parent') || 'No Parent';
    const virtualRoot: GlAccountTreeNode = {
      id: null,
      name: `${topLevelLabel} (Top Level)`,
      glCode: '',
      disabled: false,
      children: roots
    };

    return [virtualRoot];
  }

  /**
   * Filters a tree so that only matching nodes and their ancestors remain.
   */
  private filterNodes(nodes: GlAccountTreeNode[], term: string): GlAccountTreeNode[] {
    const result: GlAccountTreeNode[] = [];

    for (const node of nodes) {
      const childMatches = this.filterNodes(node.children, term);
      const nodeMatches =
        node.name.toLowerCase().includes(term) ||
        node.glCode.toLowerCase().includes(term);

      if (nodeMatches || childMatches.length > 0) {
        result.push({
          ...node,
          children: childMatches
        });
      }
    }

    return result;
  }

  /**
   * Updates the display label for the selected parent.
   */
  private refreshSelectedLabel(): void {
    const selectedId = this.inputFormControl.value;
    if (selectedId == null) {
      this.selectedLabel = '';
      return;
    }
    const account = this.glAccountList.find((item) => item.id === selectedId);
    if (account) {
      this.selectedLabel = `(${account.glCode}) ${account.name}`;
    } else {
      this.selectedLabel = '';
    }
  }
}
